import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getToken } from '@app/helpers/authProvider';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { ENV } from "@app/constants/env";
import { WoslabelSummary } from '@feature/types/label.summary.type';

// Base query for JOB_APIURL
const rawBaseQuery = fetchBaseQuery({
    baseUrl: ENV.BASE_API_URL +ENV.JOB_APIURL,
});

// Authenticated base query
const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    incomingArgs,
    api,
    extraOptions
) => {
    let args: string | FetchArgs = incomingArgs;
    if (typeof args === 'string') {
        args = { url: args };
    }
    try {
        const token = await getToken();
        if (token) {
            let headers: Record<string, string> = {};
            if ((args as FetchArgs).headers) {
                if (typeof (args as FetchArgs).headers === 'object') {
                    headers = { ...(args as FetchArgs).headers as Record<string, string> };
                }
            }
            headers['Authorization'] = `Bearer ${token}`;
            (args as FetchArgs).headers = headers;
        }
        const result = await rawBaseQuery(args, api, extraOptions);
        return result;
    } catch (error) {
        return {
            error: {
                status: 'CUSTOM_ERROR',
                error: String(error),
                data: undefined,
            } as FetchBaseQueryError,
        };
    }
};

// Interfaces
export interface AddLabelStatusEntity {
  Tbl_Tx_Booking_LineItem_ID: number;
  Service_Id: number;
  Item_Name: string;
  Length: number;
  Breadth: number;
  Height: number;
  Volume: number;
  Weight: number;
  Packge_Id: number;
  SubLineitemQty: number;
  Line_Item_HStatus: number;
  Printed: string;
  Emailed: string;
  Image_Document_Upload_Id?: string;
  Signature_Document_Upload_Id?: string;
}

export interface PackageTypeEntity {
  Pkg_Id: number;
  Pkg_Type: string;
}

export interface BookingLineItemDetail {
  Booking_LineItem_Detail_Id: number;
  Booking_LineItem_Header_Id: number;
  ItemName: string;
  Length: number;
  Breadth: number;
  Hieght: number;
  Volume: number;
  LineItem_HSStatus: number;
  Printed: string;
  Emailed: string;
  Weight: number;
}

export const addLabelsForStatusAPI = createApi({
  reducerPath: 'addLabelsForStatusAPI',
  baseQuery: authBaseQuery,
  tagTypes: ['AddLabelsForStatus', 'Address', 'PackageTypes', 'LineItemDetails'],
    endpoints: (builder) => ({
        // Get Package Types - uses JOB_APIURL
        getPackageTypes: builder.query<PackageTypeEntity[], void>({
            query: () => ({
                url: `ShippingLabel/GetWoPackagesList/12`,
                method: 'GET',
            }),
            transformResponse: (response: any) => Array.isArray(response) ? response : [],
            providesTags: ['PackageTypes'],
        }),
        
        // Get Booking Items - uses JOB_APIURL
        getBookingItems: builder.query<AddLabelStatusEntity[], number>({
            query: (serviceType) => ({
                url: `ShippingLabel/GetShippingLabels/${serviceType}`,
                method: 'GET',
            }),
            transformResponse: (response: any) => Array.isArray(response) ? response : [],
            providesTags: ['AddLabelsForStatus'],
            keepUnusedDataFor: 0, // Don't cache data
        }),
        
        // Get Line Item Details - uses JOB_APIURL
        getLineItemDetails: builder.query<BookingLineItemDetail[], number>({
            query: (headerDetailBookingId) => ({
                url: `ShippingLabel/GetLineItemDetails/${headerDetailBookingId}`,
                method: 'GET',
            }),
            transformResponse: (response: any) => Array.isArray(response) ? response : [],
            providesTags: (result, error, headerDetailBookingId) => [
                { type: 'LineItemDetails', id: headerDetailBookingId }
            ],
        }),
        
        // Create Shipping Labels - uses JOB_APIURL
        createShippingLabels: builder.mutation<any, { serviceType: number; payload: any }>({
            query: ({ serviceType, payload }) => ({
                url: `ShippingLabel/CreateShippingLabels/${serviceType}`,
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['AddLabelsForStatus'],
        }),
        
        // Update Shipping Label - uses JOB_APIURL
        updateShippingLabel: builder.mutation<any, FormData>({
            query: (formData) => ({
                url: `ShippingLabel/UpdateShippingLabel`,
                method: 'PUT',
                body: formData,
            }),
            invalidatesTags: ['AddLabelsForStatus'],
        }),
        
        // Delete Shipping Label - uses JOB_APIURL
        deleteShippingLabel: builder.mutation<any, { itemId: number; serviceType: number }>({
            query: ({ itemId, serviceType }) => ({
                url: `ShippingLabel/DeleteShippingLabel/${itemId}/${serviceType}`,
                method: 'POST',
            }),
            invalidatesTags: ['AddLabelsForStatus'],
        }),
        
        // Update Line Item Detail - uses JOB_APIURL
        updateLineItemDetail: builder.mutation<any, { userId: number; payload: any }>({
            query: ({ userId, payload }) => ({
                url: `ShippingLabel/UpdateLineItemDetail?userId=${userId}`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            }),
            invalidatesTags: (result, error, { payload }) => [
                { type: 'LineItemDetails', id: payload.Booking_LineItem_Header_Id }
            ],
        }),
        
        // Delete Line Item Detail - uses JOB_APIURL
        deleteLineItemDetail: builder.mutation<any, { detailId: number; headerId: number }>({
            query: ({ detailId }) => ({
                url: `ShippingLabel/DeleteLineItemDetail/${detailId}`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, { headerId }) => [
                { type: 'LineItemDetails', id: headerId }
            ],
        }),
        
        // Get Line Item Audit - uses JOB_APIURL
        getLineItemAudit: builder.query<any[], number>({
        query: (tblTxBookingLineItemId) => ({
        url: `ShippingLabel/GetLineItemAuditWos?tblTxBookingLineItemId=${tblTxBookingLineItemId}`,
        method: 'GET',
        }),
        transformResponse: (response: any) => Array.isArray(response) ? response : [response],
        }),
        
        // Get Labels Comments - uses JOB_APIURL
        getLabelsComments: builder.query<any[], string>({
            query: (ids) => ({
                url: `LabelsComments?ID=${ids}`,
                method: 'GET',
            }),
            transformResponse: (response: any) => Array.isArray(response) ? response : [],
        }),
        
        // Download File Labels - uses JOB_APIURL
        getDownloadFileLabels: builder.query<Blob, string>({
            query: (id) => ({
                url: `DownloadFileLabels?ID=${id}`,
                method: 'GET',
                responseHandler: (response) => response.blob(),
            }),
        }),
        // Get WOS Label Summary - uses JOB_APIURL
        getWoslabelSummary: builder.query<WoslabelSummary, number>({
            query: (serviceId) => ({
                url: `ShippingLabel/GetWoslabelSummary?serviceId=${serviceId}`, 
                method: 'GET',
                // responseHandler: (response) => response.blob(),
            }),
        }),
    }),
});

export const { 
    useGetPackageTypesQuery,
    useGetBookingItemsQuery,
    useGetLineItemDetailsQuery,
    useLazyGetLineItemDetailsQuery,
    useCreateShippingLabelsMutation,
    useUpdateShippingLabelMutation,
    useDeleteShippingLabelMutation,
    useUpdateLineItemDetailMutation,
    useDeleteLineItemDetailMutation,
    useGetLineItemAuditQuery,
    useGetLabelsCommentsQuery,
    useGetDownloadFileLabelsQuery,
    useGetWoslabelSummaryQuery,
} = addLabelsForStatusAPI;
